import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { MapPin, ListOrdered, History, Star, X, Pin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { nearestKota } from "@/data/locations";
import {
  getFavorites, getHistory, removeHistory,
  getDefaultLocation, setDefaultLocation, clearDefaultLocation,
  type PrayerLink,
} from "@/lib/prayer/favorites";
import type { PrayerVariant } from "@/lib/seo/prayer-schema";

/**
 * Bottom-of-page rail with tools for managing prayer locations:
 * geolocation, location index, favorites, and history.
 */
export function PrayerLocationToolsRail({ variant }: { variant: PrayerVariant }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [favs, setFavs] = useState<PrayerLink[]>([]);
  const [hist, setHist] = useState<PrayerLink[]>([]);
  const [def, setDef] = useState<PrayerLink | null>(null);
  const basePath = variant === "buka-puasa" ? "/jadwal-buka-puasa" : `/jadwal-${variant}`;

  useEffect(() => {
    const load = () => {
      setFavs(getFavorites().filter((x) => x.variant === variant));
      setHist(getHistory().filter((x) => x.variant === variant));
      setDef(getDefaultLocation(variant));
    };
    load();
    const onStore = () => load();
    window.addEventListener("prayer:store", onStore);
    window.addEventListener("storage", onStore);
    return () => {
      window.removeEventListener("prayer:store", onStore);
      window.removeEventListener("storage", onStore);
    };
  }, [variant]);

  function tryLocate() {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Always snap to the nearest kota — never to a province (too broad)
        // or kecamatan (often missing). Kota gives the right balance.
        const kota = nearestKota(pos.coords.latitude, pos.coords.longitude);
        setBusy(false);
        navigate({ to: `${basePath}/${kota.slug}` as never });
      },
      () => setBusy(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }

  return (
    <section className="mt-8 rounded-lg border border-border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold">Lokasi & Pengaturan Cepat</h2>
      <div className="flex flex-wrap gap-2">
        <Button onClick={tryLocate} disabled={busy} size="sm" variant="outline">
          <MapPin className="mr-2 h-4 w-4" />
          {busy ? "Mendeteksi…" : "Gunakan lokasi saya"}
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`${basePath}/lokasi` as never}>
            <ListOrdered className="mr-2 h-4 w-4" /> Indeks Lokasi
          </Link>
        </Button>
        {def && (
          <Button type="button" variant="ghost" size="sm" onClick={() => { clearDefaultLocation(); setDef(null); }}>
            <Pin className="mr-2 h-4 w-4" /> Hapus lokasi default ({def.name})
          </Button>
        )}
      </div>

      {(favs.length > 0 || hist.length > 0) && (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {favs.length > 0 && (
            <div className="rounded-md border border-prayer/30 bg-prayer/5 p-3">
              <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold text-prayer">
                <Star className="h-3.5 w-3.5 fill-prayer" /> Lokasi Favorit
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {favs.slice(0, 12).map((f) => (
                  <Link key={`${f.slug}-${f.kecSlug ?? ""}`} to={f.href as never}
                    className="rounded-full border border-prayer/30 bg-background px-2.5 py-1 text-xs hover:bg-prayer hover:text-prayer-foreground">
                    {f.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {hist.length > 0 && (
            <div className="rounded-md border border-border p-3">
              <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold">
                <History className="h-3.5 w-3.5" /> Riwayat
              </h3>
              <ul className="space-y-1">
                {hist.slice(0, 8).map((h) => (
                  <li key={`${h.slug}-${h.kecSlug ?? ""}`} className="group flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent">
                    <Link to={h.href as never} className="flex-1 truncate text-xs hover:text-prayer">{h.name}</Link>
                    <button type="button" onClick={() => { setDefaultLocation(h); setDef(h); }}
                      className="rounded p-1 text-muted-foreground hover:text-prayer" aria-label="Jadikan default" title="Jadikan default">
                      <Pin className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => { removeHistory(h); setHist((s) => s.filter((x) => x !== h)); }}
                      className="rounded p-1 text-muted-foreground hover:text-destructive" aria-label="Hapus dari riwayat">
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}